import { z } from 'zod'

export const secretKindSchema = z.enum(['secret', 'value'])
export const secretGrantTargetTypeSchema = z.enum(['user', 'api_key', 'agent', 'skill'])
export const secretGrantPermissionSchema = z.enum(['describe', 'read', 'reveal', 'inject', 'write', 'grant'])

const envSchema = z.string().min(1).max(80).regex(/^[a-zA-Z0-9_.-]+$/)
const pathSchema = z.string().min(1).max(300).default('/')
const keySchema = z.string().min(1).max(200).regex(/^[A-Za-z_][A-Za-z0-9_]*$/)

export const listSecretsSchema = z.object({
  environment: envSchema.optional(),
  path: pathSchema.optional(),
  search: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
})

export const createSecretSchema = z.object({
  environment: envSchema,
  path: pathSchema,
  key: keySchema,
  kind: secretKindSchema.default('secret'),
  value: z.string().max(100_000),
  description: z.string().max(1000).optional().nullable(),
  metadata: z.record(z.unknown()).optional(),
  reason: z.string().max(500).optional(),
})

export const updateSecretSchema = z.object({
  value: z.string().max(100_000).optional(),
  kind: secretKindSchema.optional(),
  description: z.string().max(1000).optional().nullable(),
  metadata: z.record(z.unknown()).optional(),
  expectedVersion: z.number().int().min(1),
  reason: z.string().min(1).max(500),
})

export const revealSecretSchema = z.object({
  reason: z.string().min(1).max(500),
  /** Approved reveal request id (API-key callers only) */
  requestId: z.string().min(1).max(100).optional(),
})

export const secretRevealRequestStatusSchema = z.enum(['pending', 'approved', 'denied', 'consumed', 'expired'])

export const listSecretRevealRequestsSchema = z.object({
  status: secretRevealRequestStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
})

export const rollbackSecretSchema = z.object({
  version: z.number().int().min(1),
  expectedVersion: z.number().int().min(1),
  reason: z.string().min(1).max(500),
})

export const createSecretGrantSchema = z.object({
  targetType: secretGrantTargetTypeSchema,
  targetId: z.string().min(1).max(200),
  targetDisplayName: z.string().min(1).max(200),
  environment: envSchema,
  path: pathSchema,
  keyPattern: z.string().min(1).max(200),
  permissions: z.array(secretGrantPermissionSchema).min(1).max(8),
  expiresAt: z.number().int().optional().nullable(),
  reason: z.string().max(500).optional().nullable(),
})

export const importEnvSchema = z.object({
  environment: envSchema,
  path: pathSchema,
  content: z.string().max(500_000),
  overwrite: z.boolean().default(false),
  defaultKind: secretKindSchema.default('secret'),
  reason: z.string().max(500).optional(),
})

export const exportSecretsSchema = z.object({
  environment: envSchema,
  path: pathSchema.optional(),
  reveal: z.boolean().default(false),
  reason: z.string().max(500).optional(),
})

export const injectSecretsSchema = z.object({
  environment: envSchema,
  path: pathSchema.optional(),
  /** Omit to inject every active key in environment+path the caller has an inject grant for */
  keys: z.array(keySchema).min(1).max(100).optional(),
  reason: z.string().min(1).max(500),
})

export const listSecretAuditSchema = z.object({
  environment: envSchema.optional(),
  key: keySchema.optional(),
  action: z.string().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
})

export type CreateSecretInput = z.infer<typeof createSecretSchema>
export type UpdateSecretInput = z.infer<typeof updateSecretSchema>
export type CreateSecretGrantInput = z.infer<typeof createSecretGrantSchema>
